import React, { useCallback, useEffect, useRef, useState } from 'react';

// LOOKBACK — the weekly review. Two blocks (owner call 2026-09-21): ACROSS
// ALL MISSIONS — where the week went (shares), what popped up, what was done
// and what was not, the insights — and PER MISSION, one card each with the
// same done / not done. A miss takes an optional "why", especially when it
// was traded for something else; the answer lives on the node
// (value_json.lookback_why) so the agent reads it next week. Every row is
// the node: click opens its sheet. Nothing else — no tally tiles, no habit
// strips, no priority order, no note to carry.
//
// It opens on THE WEEK THAT ENDED. ‹ › move by whole weeks; › past the last
// full week is "this week so far". The engine's seams are read through
// window at call time (fetchJson carries scope + timezone; openSheet is the
// door into the node). The dress is .lk-* in missions.css.

const inGroup = () => {
  try { return !!localStorage.getItem('missions_new:activeGroupId'); } catch { return false; }
};
const scopeQS = (scope) => (inGroup() && scope === 'me' ? '&assignee=me' : '');
const REASONS = [
  { key: 'traded', label: 'traded for…' },
  { key: 'no_time', label: 'no time' },
  { key: 'blocked', label: 'blocked' },
  { key: 'not_needed', label: 'not needed' },
  { key: 'forgot', label: 'forgot' },
];
const reasonLabel = (k) => (REASONS.find((r) => r.key === k) || {}).label || k;

function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// offset 0 = the last full week (Mon–Sun); negative = earlier; 1 = this week so far
function rangeFor(offset) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dow = today.getDay();
  const thisMonday = new Date(today); thisMonday.setDate(today.getDate() + (dow === 0 ? -6 : 1 - dow));
  if (offset >= 1) {
    const endExcl = new Date(today); endExcl.setDate(endExcl.getDate() + 1);
    return { start: localYmd(thisMonday), end: localYmd(endExcl), current: true };
  }
  const start = new Date(thisMonday); start.setDate(start.getDate() - 7 * (1 - offset));
  const end = new Date(start); end.setDate(end.getDate() + 7);
  return { start: localYmd(start), end: localYmd(end), current: false };
}
function weekTitle(range) {
  const s = new Date(range.start + 'T00:00:00');
  const e = new Date(range.end + 'T00:00:00'); e.setDate(e.getDate() - 1);
  const m = (d) => d.toLocaleDateString(undefined, { month: 'short' });
  if (s.getMonth() === e.getMonth()) return `Week of ${m(s)} ${s.getDate()} – ${e.getDate()}`;
  return `Week of ${m(s)} ${s.getDate()} – ${m(e)} ${e.getDate()}`;
}
const dayOf = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).length <= 10 ? `${iso}T12:00:00` : iso);
  return isNaN(d) ? '' : d.toLocaleDateString(undefined, { weekday: 'short' });
};
const hoursOf = (start, end) => {
  if (!start || !end) return '';
  const h = (new Date(end) - new Date(start)) / 3600000;
  if (!(h > 0)) return '';
  return h >= 23 ? 'all day' : `${Math.round(h * 2) / 2}h`;
};

async function patchLookback(range, scope, body, url) {
  const qs = `range_start=${range.start}&range_end=${range.end}${scopeQS(scope)}`;
  const res = await fetch(window.maybeScopeUrl(`${url}?${qs}`), {
    method: 'PATCH', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const r = await res.json().catch(() => ({}));
    throw new Error(r.error || res.statusText);
  }
}

// ── A row is the node ────────────────────────────────────────────────────────
function Row({ item, kind, mission, when, children }) {
  const open = () => { if (item.id) window.openSheet?.(item.id); };
  return (
    <div className={`lk-row lk-row--${kind}`} onClick={open}>
      <span className="lk-row-t">
        <span className="lk-row-title">{item.title || 'untitled'}</span>
        {mission && <span className="lk-row-m">{mission}</span>}
      </span>
      <span className="lk-row-w">{when}</span>
      {children}
    </div>
  );
}

// The why on a miss: a ghost word on hover, opens to the reasons, and
// "traded for…" opens to the week's done things. Settles to a line.
function Why({ item, doneList, onAnswer }) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(false);
  const stop = (e) => e.stopPropagation();
  if (item.why) {
    return (
      <div className="lk-why lk-why--answered" onClick={stop}>
        <span className="lk-why-ans">why · <b>{item.why.reason === 'traded' ? `traded for ${item.why.traded_for_title || '…'}` : reasonLabel(item.why.reason)}</b></span>
        <button type="button" className="lk-why-x" title="Clear" onClick={() => onAnswer(item, null)}>×</button>
      </div>
    );
  }
  return (
    <div className={`lk-why${open ? ' is-open' : ''}`} onClick={stop}>
      {!open && <button type="button" className="lk-why-ask" onClick={() => setOpen(true)}>why?</button>}
      {open && !pick && (
        <div className="lk-why-opts">
          {REASONS.map((r) => (
            <button key={r.key} type="button" onClick={() => (r.key === 'traded' ? setPick(true) : onAnswer(item, { reason: r.key }))}>{r.label}</button>
          ))}
        </div>
      )}
      {open && pick && (
        <div className="lk-why-opts">
          <span className="lk-why-for">for</span>
          {doneList.length ? doneList.map((d) => (
            <button key={d.id} type="button" onClick={() => onAnswer(item, { reason: 'traded', traded_for_id: d.id, traded_for_title: d.title })}>{d.title}</button>
          )) : <span className="lk-why-for">nothing done this week to trade for</span>}
          <button type="button" className="lk-why-back" onClick={() => setPick(false)}>‹</button>
        </div>
      )}
    </div>
  );
}

// A mission's signals for the week: what its sources saw — a count by kind,
// and the rows behind it on request. Rows are not nodes you edit here, so they
// do not open a sheet.
function Signals({ signals }) {
  const [open, setOpen] = useState(false);
  const kinds = Object.entries(signals.by_schema || {}).sort((a, b) => b[1] - a[1]);
  return (
    <>
      <SecHead right={<button type="button" className="lk-sig-toggle" onClick={() => setOpen((v) => !v)}>{open ? 'fewer' : 'show'}</button>}>
        signals · {kinds.map(([k, n]) => `${n} ${k.replace(/[_:-]/g, ' ')}`).join(' · ')}
      </SecHead>
      {open && (signals.rows || []).map((r) => (
        <div key={r.node_id} className="lk-row lk-row--signal">
          <span className="lk-row-t"><span className="lk-row-title">{r.title || r.kind || 'signal'}</span><span className="lk-row-m">{(r.schema_name || '').replace(/[_:-]/g, ' ')}</span></span>
          <span className="lk-row-w lk-row-w--plain">{dayOf(r.occurred_at)}</span>
        </div>
      ))}
    </>
  );
}

function SecHead({ children, right }) {
  return <div className="lk-sec-head"><span>{children}</span>{right != null && <span>{right}</span>}</div>;
}

export default function Lookback({ active }) {
  const [offset, setOffset] = useState(0);
  const [scope, setScope] = useState('me');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const generatingRef = useRef(false);
  const range = rangeFor(offset);

  useEffect(() => {
    const again = () => setEpoch((n) => n + 1);
    document.addEventListener('missions:refresh-lookback', again);
    return () => document.removeEventListener('missions:refresh-lookback', again);
  }, []);
  useEffect(() => { if (active) setEpoch((n) => n + 1); }, [active]);

  const generate = useCallback(async ({ silent = false, force = false } = {}) => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    const qs = `range_start=${range.start}&range_end=${range.end}${scopeQS(scope)}`;
    if (!silent) setGenerating(true);
    try {
      if (force) await fetch(window.maybeScopeUrl(`/api/lookback/cache?${qs}`), { method: 'DELETE', credentials: 'same-origin' });
      const res = await fetch(window.maybeScopeUrl(`/api/lookback/generate?${qs}`), {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || res.statusText); }
      setData(await res.json());
    } catch (e) {
      console.warn('[lookback] generate failed:', e.message);
      if (!silent) alert(`Couldn't generate — ${e.message}`);
    } finally {
      setGenerating(false);
      generatingRef.current = false;
    }
  }, [range.start, range.end, scope]);

  useEffect(() => {
    if (!active || !epoch) return undefined;
    let live = true;
    setError(null);
    window.fetchJson(`/api/lookback?range_start=${range.start}&range_end=${range.end}${scopeQS(scope)}`)
      .then((d) => {
        if (!live) return;
        setData(d);
        if (d.insights === null) { setGenerating(true); generate({ silent: true }); }
      })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [active, epoch, range.start, range.end, scope, generate]);

  // the why: written on the node, mirrored in every list it appears in
  const answerWhy = async (item, why) => {
    const value = why ? { ...why, week: range.start } : null;
    try {
      await window.putJson(`/api/items/${item.id}`, { value_json: { lookback_why: value } });
      setData((d) => ({
        ...d,
        missions: d.missions.map((m) => ({ ...m, not_done: (m.not_done || []).map((n) => (n.id === item.id ? { ...n, why: value } : n)) })),
      }));
    } catch (e) { alert(`Couldn't save — ${e.message}`); }
  };
  const tickInsight = async (index, on) => {
    try {
      await patchLookback(range, scope, { index, status: on ? 'accepted' : null }, '/api/lookback/insight');
      setData((d) => ({ ...d, insights: d.insights.map((p, i) => (i === index ? { ...p, status: on ? 'accepted' : null } : p)) }));
    } catch (e) { alert(`Couldn't update — ${e.message}`); }
  };

  if (!active && data === null) return null;

  const missions = data?.missions || [];
  const popped = data?.popped || [];
  const doneAll = missions.flatMap((m) => (m.wins || []).map((w) => ({ ...w, mission: m.name })));
  const missAll = missions.flatMap((m) => (m.not_done || []).map((n) => ({ ...n, mission: m.name })));
  const doneForTrade = [...doneAll, ...popped.filter((p) => p.kind === 'event' || p.done).map((p) => ({ id: p.id, title: p.title }))];
  // share of the week: each mission's part of what happened — its completions,
  // what popped up for it, and its SIGNALS (the evidence that is not
  // self-reported: runs, pushes, whatever its sources watch). A share, not
  // hours; the app has no hours.
  const poppedDoneBy = new Map();
  for (const p of popped) if (p.kind === 'action' && p.done && p.mission_name) poppedDoneBy.set(p.mission_name, (poppedDoneBy.get(p.mission_name) || 0) + 1);
  const sigCount = (m) => (m.signals && m.signals.count) || 0;
  const shares = missions.map((m) => ({ name: m.name, n: m.completions + (poppedDoneBy.get(m.name) || 0) + sigCount(m) })).filter((x) => x.n > 0).sort((a, b) => b.n - a.n);
  const shareTotal = shares.reduce((s, x) => s + x.n, 0);
  const signalTotal = missions.reduce((s, m) => s + sigCount(m), 0);
  const cards = missions.filter((m) => (m.wins || []).length || (m.not_done || []).length || sigCount(m));
  const insights = data?.insights;
  // AN EMPTY WEEK SAYS IT ONCE. Sections with nothing in them do not render a
  // "nothing" line each; and a week with nothing at all is one sentence.
  const emptyWeek = !doneAll.length && !missAll.length && !popped.length && !signalTotal;
  const showInsights = Array.isArray(insights) ? insights.length > 0 : generating;

  return (
    <div className="lk">
      {/* the title is on the page's left edge, with everything under it; the arrows follow it */}
      <div className="lk-bar">
        <h1 className="lk-week text-title">{weekTitle(range)}</h1>
        <span className="lk-nav">
          <button type="button" aria-label="Earlier week" onClick={() => { setOffset((o) => o - 1); setEpoch((n) => n + 1); }}>‹</button>
          <button type="button" aria-label="Later week" disabled={offset >= 1} onClick={() => { setOffset((o) => Math.min(1, o + 1)); setEpoch((n) => n + 1); }}>›</button>
        </span>
        <span className="lk-sub">{range.current ? 'this week so far' : offset === 0 ? 'the week that ended' : ''}</span>
        {inGroup() && (
          <span className="lk-scope">
            <button type="button" className={scope === 'me' ? 'on' : ''} onClick={() => { setScope('me'); setEpoch((n) => n + 1); }}>me</button>
            <button type="button" className={scope === 'all' ? 'on' : ''} onClick={() => { setScope('all'); setEpoch((n) => n + 1); }}>group</button>
          </span>
        )}
      </div>

      {error && <div className="missions-empty">Couldn't load lookback — {error}</div>}
      {!error && !data && <div className="loading">loading…</div>}
      {!error && data && emptyWeek && (
        <div className="lk-nothing">
          <div className="lk-nothing-line">Nothing landed on this week{inGroup() && scope === 'me' ? ' for you' : ''} — no actions done, none dated, nothing popped up, no signals.</div>
        </div>
      )}
      {!error && data && !emptyWeek && (
        <>
          <section className="lk-block">
            <span className="lk-kicker">Across all missions</span>

            {shareTotal > 0 && (
              <div className="lk-sec">
                <SecHead right={signalTotal ? `share of the week · completions and ${signalTotal} signal${signalTotal === 1 ? '' : 's'}` : 'share of the week'}>where the time went</SecHead>
                <div className="lk-share">
                  {shares.map((x, k) => <i key={x.name} style={{ width: `${(x.n / shareTotal) * 100}%`, '--o': 1 - k * 0.18 }} title={`${x.name} · ${Math.round((x.n / shareTotal) * 100)}%`} />)}
                </div>
                <div className="lk-share-legend">
                  {shares.map((x, k) => <span key={x.name}><i style={{ '--o': 1 - k * 0.18 }} />{x.name} <b>{Math.round((x.n / shareTotal) * 100)}%</b></span>)}
                </div>
              </div>
            )}

            {popped.length > 0 && (
              <div className="lk-sec">
                <SecHead right="not planned when the week began">what popped up</SecHead>
                {popped.map((p) => (
                  <Row key={p.id} item={p} kind={p.kind === 'event' ? 'event' : p.done ? 'done' : 'miss'}
                       mission={p.kind === 'event' ? 'event' : p.mission_name}
                       when={p.kind === 'event' ? `${dayOf(p.when)}${hoursOf(p.when, p.end_at) ? ' · ' + hoursOf(p.when, p.end_at) : ''}` : `${dayOf(p.when)}${p.done ? ' · done' : ''}`} />
                ))}
              </div>
            )}

            {(doneAll.length > 0 || missAll.length > 0) && (
              <div className={`lk-sec${doneAll.length && missAll.length ? ' lk-two' : ''}`}>
                {doneAll.length > 0 && (
                  <div>
                    <SecHead right={`${doneAll.length} of ${doneAll.length + missAll.length}`}>done</SecHead>
                    {doneAll.map((w) => <Row key={w.id} item={w} kind="done" mission={w.mission} when={dayOf(w.when)} />)}
                  </div>
                )}
                {missAll.length > 0 && (
                  <div>
                    <SecHead right={doneAll.length ? String(missAll.length) : `${missAll.length} · nothing done`}>not done</SecHead>
                    {missAll.map((n) => (
                      <Row key={n.id} item={n} kind="miss" mission={n.mission} when={n.state === 'dropped' ? 'dropped' : `${dayOf(n.when)}${n.weeks_late > 1 ? ` · ${n.weeks_late} weeks` : ''}`}>
                        <Why item={n} doneList={doneForTrade} onAnswer={answerWhy} />
                      </Row>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showInsights && (
              <div className="lk-sec">
                <SecHead right={(
                  <span className="lk-ins-right">
                    tick the ones worth keeping
                    <button type="button" className="lk-regen" title="Read the week again" disabled={generating} onClick={() => generate({ force: true })}>↻</button>
                  </span>
                )}>insights</SecHead>
                {!Array.isArray(insights)
                  ? <div className="lk-empty">reading the week…</div>
                  : insights.map((p, i) => (
                    <button key={i} type="button" className="lk-ins" aria-pressed={p.status === 'accepted'} onClick={() => tickInsight(i, p.status !== 'accepted')}>
                      <span className="lk-ins-box" />
                      <span>
                        <span className="lk-ins-t">{p.observation}</span>
                        {p.context && <span className="lk-ins-sub">{p.context}</span>}
                      </span>
                    </button>
                  ))}
              </div>
            )}
          </section>

          {cards.length > 0 && (
          <section className="lk-block">
            <span className="lk-kicker">Per mission</span>
            {cards.length ? (
              <div className="lk-cards">
                {cards.map((m) => (
                  <div className="lk-card" key={m.id}>
                    <div className="lk-card-head">
                      <h3 className="lk-card-name">{m.name}</h3>
                      <span className="lk-card-n">
                        {(m.wins.length + (m.not_done || []).length) > 0 && <><b>{m.wins.length}</b> of {m.wins.length + (m.not_done || []).length}</>}
                        {sigCount(m) > 0 && <>{(m.wins.length + (m.not_done || []).length) > 0 ? ' · ' : ''}<b>{sigCount(m)}</b> signal{sigCount(m) === 1 ? '' : 's'}</>}
                      </span>
                    </div>
                    {sigCount(m) > 0 && <Signals signals={m.signals} />}
                    {m.wins.length > 0 && <SecHead>done</SecHead>}
                    {m.wins.map((w) => <Row key={w.id} item={w} kind="done" when={dayOf(w.when)} />)}
                    {(m.not_done || []).length > 0 && <SecHead>not done</SecHead>}
                    {(m.not_done || []).map((n) => (
                      <Row key={n.id} item={n} kind="miss" when={n.state === 'dropped' ? 'dropped' : `${dayOf(n.when)}${n.weeks_late > 1 ? ` · ${n.weeks_late} weeks` : ''}`}>
                        <Why item={n} doneList={doneForTrade} onAnswer={answerWhy} />
                      </Row>
                    ))}
                  </div>
                ))}
              </div>
            ) : null}
          </section>
          )}
        </>
      )}
    </div>
  );
}
